import { useEffect, useId, useMemo } from "react"
import { pluralize } from "@/utils/text-helpers"
import { SelectInput } from "@ui/forms"
import { EmailIntegrationSelectField } from "@ui/forms/fields/email-integration-select-field"
import { Form, FormField } from "@ui/shad"
import { cn } from "@ui/utils"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { useFetchSequenceEnrollmentStats } from "@/hooks/api/sequence-enrollment-stats"
import { useFetchSequences } from "@/hooks/api/sequences/queries"
import { SmsIntegrationField } from "@/components/automations/edit-panel/actions/sms-integration-field"
import { SimpleFormDialog } from "@/components/dialogs"

type BulkAddToSequenceFormValues = {
  sequence_id: string
  email_integration_id: string | null
  sms_integration: string | null
}

interface BulkAddToSequenceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (sequenceId: string, emailIntegrationId?: string | null, smsIntegration?: string | null) => void
  isSubmitting: boolean
  selectedHouseholdCount: number
}

export function BulkAddToSequenceDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  selectedHouseholdCount,
}: BulkAddToSequenceDialogProps) {
  const dialogFormId = useId()

  const form = useForm<BulkAddToSequenceFormValues>({
    defaultValues: {
      sequence_id: "",
      email_integration_id: null,
      sms_integration: null,
    },
  })
  const sequenceId = form.watch("sequence_id")
  const emailIntegrationId = form.watch("email_integration_id")
  const smsIntegration = form.watch("sms_integration")

  const { data: sequencesResponse, isLoading: isLoadingSequences } = useFetchSequences(open)
  const activeSequences = useMemo(
    () => sequencesResponse?.sequences.filter((sequence) => sequence.active) ?? [],
    [sequencesResponse?.sequences]
  )
  const selectedSequence = useMemo(
    () => activeSequences.find((sequence) => sequence.id === sequenceId),
    [activeSequences, sequenceId]
  )
  const selectedSequenceHasEmailSteps =
    selectedSequence?.sequence_steps.some((step) => step.step_type === "email") ?? false
  const selectedSequenceHasSmsSteps = selectedSequence?.sequence_steps.some((step) => step.step_type === "sms") ?? false
  const { data: enrollmentStats, isLoading: isLoadingEnrollmentStats } = useFetchSequenceEnrollmentStats({
    emailIntegrationId,
    enabled: open && selectedSequenceHasEmailSteps && Boolean(emailIntegrationId),
  })

  useEffect(() => {
    if (open) return
    form.reset({ sequence_id: "", email_integration_id: null, sms_integration: null })
  }, [form, open])

  useEffect(() => {
    form.setValue("email_integration_id", null, { shouldDirty: false, shouldTouch: false })
    form.setValue("sms_integration", null, { shouldDirty: false, shouldTouch: false })
  }, [form, sequenceId])

  const sequencePlaceholder = useMemo(() => {
    if (isLoadingSequences) return "Loading sequences..."
    if (activeSequences.length === 0) return "No active sequences available"
    return "Select a sequence"
  }, [activeSequences.length, isLoadingSequences])

  const sequenceOptions = useMemo(
    () =>
      activeSequences.map((sequence) => ({
        value: sequence.id,
        label: sequence.name,
      })),
    [activeSequences]
  )

  const remainingSlots = enrollmentStats?.remaining_enrollment_slots
  const exceedsRemainingSlots =
    selectedSequenceHasEmailSteps &&
    typeof remainingSlots === "number" &&
    selectedHouseholdCount > remainingSlots &&
    remainingSlots >= 0

  const isConfirmDisabled =
    !sequenceId ||
    isSubmitting ||
    activeSequences.length === 0 ||
    (selectedSequenceHasEmailSteps && Boolean(emailIntegrationId) && isLoadingEnrollmentStats) ||
    (selectedSequenceHasSmsSteps && !smsIntegration) ||
    exceedsRemainingSlots

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return
    onOpenChange(nextOpen)
  }

  const handleSubmit = form.handleSubmit((values) => {
    if (exceedsRemainingSlots || !values.sequence_id) return
    if (selectedSequenceHasSmsSteps && !values.sms_integration) {
      toast.error("Please select a phone integration for SMS steps")
      return
    }
    onConfirm(
      values.sequence_id,
      selectedSequenceHasEmailSteps ? values.email_integration_id : undefined,
      selectedSequenceHasSmsSteps ? values.sms_integration : undefined
    )
  })

  return (
    <SimpleFormDialog
      title={<>Add {pluralize(selectedHouseholdCount, "household")} to a sequence</>}
      open={open}
      onOpenChange={handleOpenChange}
      action={{
        label: selectedHouseholdCount === 1 ? "Add household" : "Add households",
        form: `${dialogFormId}-dialog-bulk-add-to-sequence-dialog-133`,
        disabled: isConfirmDisabled,
        pending: isSubmitting,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: isSubmitting }}
    >
      <Form {...form}>
        <form
          id={`${dialogFormId}-dialog-bulk-add-to-sequence-dialog-133`}
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-2"
        >
          <>
            {exceedsRemainingSlots && (
              <div className="bg-red-background border-red-accent text-red-foreground mb-2 border-l-2 p-2">
                <p className="text-sm font-medium">
                  {selectedHouseholdCount.toLocaleString()} is greater than the {remainingSlots?.toLocaleString()} open
                  enrollment slots available for the selected email account. Please select fewer households to continue.
                </p>
              </div>
            )}
            <div className={cn("space-y-2", exceedsRemainingSlots ? "opacity-60" : "", "transition-opacity")}>
              <div>
                <FormField
                  control={form.control}
                  name="sequence_id"
                  render={({ field }) => (
                    <SelectInput
                      id="bulk-sequence-selector"
                      label="Sequence"
                      value={field.value}
                      onChange={(value: string | null) => field.onChange(value ?? "")}
                      options={isLoadingSequences ? undefined : sequenceOptions}
                      placeholder={sequencePlaceholder}
                      disabled={exceedsRemainingSlots || isLoadingSequences || activeSequences.length === 0}
                    />
                  )}
                />
                <p className="text-muted-foreground mt-1 ml-1 text-xs">
                  Only the head of household will be emailed. To include spouses or other household members, enroll each
                  household individually.
                </p>
              </div>
              {selectedSequence && selectedSequenceHasEmailSteps && (
                <FormField
                  control={form.control}
                  name="email_integration_id"
                  render={({ field }) => (
                    <EmailIntegrationSelectField
                      sequence={selectedSequence}
                      field={field}
                      autoSelect={false}
                      allowEmpty
                      emptyOptionLabel="Use sequence default"
                    />
                  )}
                />
              )}
              {selectedSequenceHasSmsSteps && (
                <FormField
                  control={form.control}
                  name="sms_integration"
                  render={({ field }) => (
                    <SmsIntegrationField
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                      label="Send SMS from"
                      required
                      autoSelect={false}
                    />
                  )}
                />
              )}
              {!isLoadingSequences && activeSequences.length === 0 && (
                <p className="text-muted-foreground text-sm">Activate a sequence to enable bulk enrollment.</p>
              )}
            </div>
          </>
        </form>
      </Form>
    </SimpleFormDialog>
  )
}
